import { CodeBlock, InfoBox, SectionTitle, SubsectionTitle, Table, Badge } from './shared';

export function SecurityContent() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-bold text-white mb-4">安全加固</h1>
        <p className="text-lg text-slate-400 leading-relaxed">
          了解如何加固 OpenClaw 网关的安全性，保护你的 API 免受各种攻击。
        </p>
      </div>

      <SectionTitle id="security-overview">安全概览</SectionTitle>
      <div className="grid md:grid-cols-3 gap-4">
        {[
          { icon: 'ri-shield-check-line', title: '认证授权', desc: '多层次的身份验证和权限控制', color: 'green' },
          { icon: 'ri-lock-line', title: '数据加密', desc: 'TLS/SSL 加密传输和存储', color: 'cyan' },
          { icon: 'ri-bug-line', title: '攻击防护', desc: '防止 DDoS、注入、XSS 等攻击', color: 'purple' }
        ].map((item, idx) => (
          <div key={idx} className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-6">
            <div className={`w-12 h-12 bg-${item.color}-500/20 rounded-lg flex items-center justify-center mb-4`}>
              <i className={`${item.icon} text-2xl text-${item.color}-400`}></i>
            </div>
            <h3 className="font-semibold text-white mb-2">{item.title}</h3>
            <p className="text-sm text-slate-400">{item.desc}</p>
          </div>
        ))}
      </div>

      <SectionTitle id="https-ssl">HTTPS/SSL 配置</SectionTitle>
      <p className="text-slate-300 leading-relaxed mb-4">
        启用 HTTPS 是保护 API 安全的第一步，确保所有数据传输都经过加密。
      </p>

      <SubsectionTitle id="ssl-setup">配置 SSL 证书</SubsectionTitle>
      <CodeBlock
        language="yaml"
        code={`# gateway.yaml
ssl:
  enabled: true
  cert_file: "/etc/openclaw/ssl/cert.pem"
  key_file: "/etc/openclaw/ssl/key.pem"
  
  # 强制使用 HTTPS
  force_https: true
  
  # 最低 TLS 版本
  min_version: "1.2"  # 禁用 TLS 1.0 和 1.1
  
  # 推荐的加密套件
  cipher_suites:
    - "TLS_ECDHE_RSA_WITH_AES_128_GCM_SHA256"
    - "TLS_ECDHE_RSA_WITH_AES_256_GCM_SHA384"
    - "TLS_ECDHE_RSA_WITH_CHACHA20_POLY1305"
  
  # HSTS（HTTP Strict Transport Security）
  hsts:
    enabled: true
    max_age: 31536000  # 1 年
    include_subdomains: true
    preload: true`}
      />

      <SubsectionTitle id="ssl-certificate">获取 SSL 证书</SubsectionTitle>
      <CodeBlock
        language="bash"
        code={`# 使用 Let's Encrypt 获取免费证书
sudo apt-get install certbot

# 获取证书（需要域名）
sudo certbot certonly --standalone -d yourdomain.com

# 证书位置
# 证书: /etc/letsencrypt/live/yourdomain.com/fullchain.pem
# 私钥: /etc/letsencrypt/live/yourdomain.com/privkey.pem

# 自动续期
sudo certbot renew --dry-run

# 设置定时任务自动续期
sudo crontab -e
# 添加: 0 0 * * * certbot renew --quiet`}
      />

      <InfoBox type="info" title="证书续期">
        Let's Encrypt 证书有效期为 90 天，建议设置自动续期任务。Certbot 会在证书到期前 30 天自动尝试续期。
      </InfoBox>

      <SectionTitle id="rate-limiting">限流防护</SectionTitle>
      <p className="text-slate-300 leading-relaxed mb-4">
        限流可以防止 API 滥用和 DDoS 攻击，保护系统稳定性。
      </p>

      <SubsectionTitle id="rate-limit-config">配置限流规则</SubsectionTitle>
      <CodeBlock
        language="yaml"
        code={`# gateway.yaml
rate_limit:
  enabled: true
  
  # 全局限流
  global:
    requests_per_minute: 1000
    burst: 100
  
  # 按 IP 限流
  per_ip:
    enabled: true
    requests_per_minute: 60
    burst: 10
    # IP 白名单
    whitelist:
      - "192.168.1.0/24"
      - "10.0.0.0/8"
    # IP 黑名单
    blacklist:
      - "1.2.3.4"
  
  # 按 API Key 限流
  per_key:
    enabled: true
    requests_per_minute: 100
    burst: 20
  
  # 按路由限流
  per_route:
    - path: "/api/chat"
      requests_per_minute: 30
    - path: "/api/admin/*"
      requests_per_minute: 10
  
  # 限流算法
  algorithm: "sliding_window"  # fixed_window, sliding_window, token_bucket
  
  # 存储后端（分布式限流）
  storage: "redis"
  redis:
    host: "localhost"
    port: 6379
    db: 0`}
      />

      <SubsectionTitle id="rate-limit-response">限流响应</SubsectionTitle>
      <p className="text-slate-300 leading-relaxed mb-4">
        当请求超过限流阈值时，网关会返回 429 状态码：
      </p>
      <CodeBlock
        language="json"
        code={`{
  "error": "rate_limit_exceeded",
  "message": "请求过于频繁，请稍后再试",
  "retry_after": 60,
  "limit": 60,
  "remaining": 0,
  "reset": 1704067260
}`}
      />

      <SectionTitle id="ddos-protection">DDoS 防护</SectionTitle>
      <p className="text-slate-300 leading-relaxed mb-4">
        配置多层防护策略，抵御分布式拒绝服务攻击。
      </p>

      <SubsectionTitle id="ddos-config">DDoS 防护配置</SubsectionTitle>
      <CodeBlock
        language="yaml"
        code={`# gateway.yaml
ddos_protection:
  enabled: true
  
  # 连接限制
  connection_limit:
    max_connections: 10000
    max_connections_per_ip: 100
  
  # 请求大小限制
  request_limits:
    max_body_size: "10MB"
    max_header_size: "8KB"
    max_uri_length: 2048
  
  # 慢速攻击防护
  slow_attack:
    # 请求超时
    request_timeout: 30
    # 读取超时
    read_timeout: 10
    # 写入超时
    write_timeout: 10
  
  # 异常检测
  anomaly_detection:
    enabled: true
    # 异常请求阈值
    threshold: 100
    # 检测窗口（秒）
    window: 60
    # 自动封禁
    auto_ban:
      enabled: true
      duration: 3600  # 1 小时`}
      />

      <SubsectionTitle id="cloudflare">使用 Cloudflare</SubsectionTitle>
      <p className="text-slate-300 leading-relaxed mb-4">
        推荐在网关前部署 Cloudflare 等 CDN 服务，提供额外的 DDoS 防护：
      </p>
      <ul className="space-y-2 text-slate-300">
        <li className="flex items-start gap-2">
          <i className="ri-checkbox-circle-line text-green-400 mt-1"></i>
          <span>自动 DDoS 防护和流量清洗</span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-checkbox-circle-line text-green-400 mt-1"></i>
          <span>全球 CDN 加速</span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-checkbox-circle-line text-green-400 mt-1"></i>
          <span>Web 应用防火墙（WAF）</span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-checkbox-circle-line text-green-400 mt-1"></i>
          <span>Bot 管理</span>
        </li>
      </ul>

      <SectionTitle id="input-validation">输入验证</SectionTitle>
      <p className="text-slate-300 leading-relaxed mb-4">
        严格验证所有输入数据，防止注入攻击。
      </p>

      <SubsectionTitle id="validation-config">配置输入验证</SubsectionTitle>
      <CodeBlock
        language="yaml"
        code={`# gateway.yaml
input_validation:
  enabled: true
  
  # 请求体验证
  body:
    # 最大大小
    max_size: "10MB"
    # 允许的 Content-Type
    allowed_content_types:
      - "application/json"
      - "application/x-www-form-urlencoded"
      - "multipart/form-data"
  
  # 参数验证
  parameters:
    # 最大参数数量
    max_count: 100
    # 参数名称白名单（正则）
    name_pattern: "^[a-zA-Z0-9_-]+$"
    # 参数值最大长度
    max_value_length: 1000
  
  # SQL 注入防护
  sql_injection:
    enabled: true
    # 危险关键词
    blocked_keywords:
      - "DROP TABLE"
      - "DELETE FROM"
      - "UNION SELECT"
      - "'; --"
  
  # XSS 防护
  xss:
    enabled: true
    # 自动转义 HTML
    escape_html: true
    # 危险标签
    blocked_tags:
      - "<script>"
      - "<iframe>"
      - "javascript:"
  
  # 路径遍历防护
  path_traversal:
    enabled: true
    blocked_patterns:
      - "../"
      - "..\\\\"`}
      />

      <SectionTitle id="cors-security">CORS 安全配置</SectionTitle>
      <p className="text-slate-300 leading-relaxed mb-4">
        正确配置 CORS，防止跨域攻击。
      </p>

      <CodeBlock
        language="yaml"
        code={`# gateway.yaml
cors:
  enabled: true
  
  # 允许的源（不要使用 * 在生产环境）
  origins:
    - "https://yourdomain.com"
    - "https://app.yourdomain.com"
  
  # 允许的方法
  methods:
    - "GET"
    - "POST"
    - "PUT"
    - "DELETE"
  
  # 允许的 Headers
  headers:
    - "Content-Type"
    - "Authorization"
  
  # 暴露的 Headers
  expose_headers:
    - "X-Request-ID"
  
  # 是否允许凭证
  credentials: true
  
  # 预检请求缓存时间
  max_age: 86400`}
      />

      <InfoBox type="warning" title="CORS 安全提醒">
        <ul className="space-y-1 mt-2">
          <li>• 生产环境不要使用 <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">origins: ["*"]</code></li>
          <li>• 明确指定允许的域名</li>
          <li>• 谨慎使用 <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">credentials: true</code></li>
          <li>• 定期审查 CORS 配置</li>
        </ul>
      </InfoBox>

      <SectionTitle id="security-headers">安全响应头</SectionTitle>
      <p className="text-slate-300 leading-relaxed mb-4">
        添加安全相关的 HTTP 响应头，增强浏览器安全性。
      </p>

      <CodeBlock
        language="yaml"
        code={`# gateway.yaml
security_headers:
  enabled: true
  
  headers:
    # 防止点击劫持
    X-Frame-Options: "DENY"
    
    # XSS 防护
    X-XSS-Protection: "1; mode=block"
    
    # 内容类型嗅探防护
    X-Content-Type-Options: "nosniff"
    
    # 引用策略
    Referrer-Policy: "strict-origin-when-cross-origin"
    
    # 权限策略
    Permissions-Policy: "geolocation=(), microphone=(), camera=()"
    
    # 内容安全策略
    Content-Security-Policy: "default-src 'self'; script-src 'self' 'unsafe-inline'; style-src 'self' 'unsafe-inline'"
    
    # HSTS
    Strict-Transport-Security: "max-age=31536000; includeSubDomains; preload"`}
      />

      <SectionTitle id="api-key-security">API Key 安全</SectionTitle>
      <div className="space-y-4">
        <SubsectionTitle id="key-rotation">密钥轮换</SubsectionTitle>
        <p className="text-slate-300 leading-relaxed mb-4">
          定期轮换 API Key，降低密钥泄露风险：
        </p>
        <CodeBlock
          language="bash"
          code={`# 创建新的 API Key（设置过期时间）
openclaw gateway api-key create --name "App v2" --expires 90d

# 列出即将过期的 API Key
openclaw gateway api-key list --expiring 30d

# 批量轮换 API Key
openclaw gateway api-key rotate --older-than 180d`}
        />

        <SubsectionTitle id="key-permissions">最小权限原则</SubsectionTitle>
        <p className="text-slate-300 leading-relaxed mb-4">
          为每个 API Key 分配最小必要权限：
        </p>
        <CodeBlock
          language="bash"
          code={`# 创建只读 API Key
openclaw gateway api-key create \\
  --name "Analytics" \\
  --permissions "read" \\
  --routes "/api/stats,/api/metrics"

# 创建受限 API Key
openclaw gateway api-key create \\
  --name "Mobile App" \\
  --permissions "read,write" \\
  --routes "/api/chat,/api/user" \\
  --rate-limit 100`}
        />
      </div>

      <SectionTitle id="audit-logging">审计日志</SectionTitle>
      <p className="text-slate-300 leading-relaxed mb-4">
        记录所有安全相关事件，便于事后审计和分析。
      </p>

      <CodeBlock
        language="yaml"
        code={`# gateway.yaml
audit_logging:
  enabled: true
  
  # 记录的事件类型
  events:
    - "auth_success"
    - "auth_failure"
    - "rate_limit_exceeded"
    - "invalid_token"
    - "suspicious_activity"
    - "api_key_created"
    - "api_key_revoked"
  
  # 日志输出
  output:
    - type: "file"
      path: "/var/log/openclaw/audit.log"
      format: "json"
    - type: "syslog"
      host: "syslog.example.com"
      port: 514
    - type: "elasticsearch"
      url: "http://elasticsearch:9200"
      index: "openclaw-audit"
  
  # 日志保留
  retention:
    days: 90
    max_size: "10GB"`}
      />

      <SubsectionTitle id="audit-query">查询审计日志</SubsectionTitle>
      <CodeBlock
        language="bash"
        code={`# 查看最近的认证失败
openclaw gateway audit query --event auth_failure --limit 100

# 查看特定 IP 的活动
openclaw gateway audit query --ip 1.2.3.4

# 查看特定时间范围的日志
openclaw gateway audit query --from "2024-01-01" --to "2024-01-31"

# 导出审计日志
openclaw gateway audit export --format csv --output audit.csv`}
      />

      <SectionTitle id="security-monitoring">安全监控</SectionTitle>
      <p className="text-slate-300 leading-relaxed mb-4">
        实时监控安全指标，及时发现异常。
      </p>

      <div className="grid md:grid-cols-2 gap-4">
        {[
          { label: '认证失败率', threshold: '> 5%', action: '触发告警' },
          { label: '限流触发次数', threshold: '> 100/分钟', action: '自动封禁' },
          { label: '异常请求', threshold: '> 50/分钟', action: '人工审查' },
          { label: '慢请求', threshold: '> 10秒', action: '性能分析' }
        ].map((item, idx) => (
          <div key={idx} className="bg-slate-900/50 border border-slate-800/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-400 text-sm">{item.label}</span>
              <Badge variant="warning">{item.threshold}</Badge>
            </div>
            <div className="text-white font-medium">{item.action}</div>
          </div>
        ))}
      </div>

      <SectionTitle id="security-checklist">安全检查清单</SectionTitle>
      <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-6">
        <div className="space-y-3">
          {[
            '启用 HTTPS 并强制使用 TLS 1.2+',
            '配置强密码和长密钥（至少 32 字符）',
            '启用多因素认证（MFA）',
            '配置限流和 DDoS 防护',
            '启用输入验证和 XSS 防护',
            '正确配置 CORS 和安全响应头',
            '定期轮换 API Key 和证书',
            '启用审计日志和安全监控',
            '最小权限原则分配权限',
            '定期进行安全审计和渗透测试',
            '保持系统和依赖更新',
            '制定安全事件响应计划'
          ].map((item, idx) => (
            <label key={idx} className="flex items-start gap-3 cursor-pointer group">
              <input type="checkbox" className="mt-1 w-4 h-4 rounded border-slate-600 bg-slate-800 text-green-500 focus:ring-green-500 focus:ring-offset-slate-900" />
              <span className="text-slate-300 group-hover:text-white transition-colors">{item}</span>
            </label>
          ))}
        </div>
      </div>

      <InfoBox type="success" title="定期安全审查">
        建议每季度进行一次全面的安全审查，包括配置检查、日志分析、漏洞扫描和渗透测试。
      </InfoBox>
    </div>
  );
}

export default SecurityContent;